import { useState } from "react";
import { apiFetch } from "../../../context/AuthContext";
import { parseCSV, serializePreviewToCSV } from "../utils/csvUtils";
import * as XLSX from "xlsx";

const useCSVImport = ({ BASE_URL, language, profile, viewType, selectedDate, employees, loadAttendance, showAlert }) => {
  const [showImportModal, setShowImportModal] = useState(false);
  const [importPreview, setImportPreview] = useState([]);
  const [importResult, setImportResult] = useState(null);
  const [importActiveTab, setImportActiveTab] = useState("preview");
  const [isImporting, setIsImporting] = useState(false);
  const [duplicateSelections, setDuplicateSelections] = useState({});
  const [isResolvingDuplicates, setIsResolvingDuplicates] = useState(false);

  const handleImportFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    e.target.value = "";

    const isXlsx =
      file.name.toLowerCase().endsWith(".xlsx") ||
      file.name.toLowerCase().endsWith(".xls");

    if (isXlsx) {
      // ✅ Parse Excel binary properly with the xlsx library
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const arrayBuffer = event.target?.result;
          const workbook = XLSX.read(arrayBuffer, { type: "array" });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          // The template has title/legend rows above the real header row —
          // find the row that holds the clientCode header and read from there.
          const normalizeHeader = (v) => String(v || "").replace(/[^a-z0-9]/gi, "").toLowerCase();
          const grid = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: "", raw: false });
          const headerIdx = grid.findIndex((r) =>
            (r || []).some((c) => normalizeHeader(c) === "clientcode"),
          );
          // sheet_to_json returns array of objects keyed by header row
          const rows = XLSX.utils.sheet_to_json(worksheet, {
            defval: "",          // empty cells become "" not undefined
            raw: false,          // format dates/numbers as strings
            range: headerIdx > 0 ? headerIdx : 0,
          }).filter((r) =>
            // drop the template's "paste additional rows above" footer and blank rows
            Object.values(r).some((v) => String(v).trim() !== "") &&
            !String(Object.values(r)[0] || "").includes("Paste additional rows"),
          );
          setImportPreview(rows);
          setImportResult(null);
          setImportActiveTab("preview");
          setShowImportModal(true);
        } catch (err) {
          console.error("❌ Failed to parse xlsx file:", err);
          showAlert(
            language === "ar" ? "خطأ" : "Parse Error",
            language === "ar"
              ? "تعذّر قراءة ملف Excel. تأكد أنه ملف .xlsx صحيح."
              : "Failed to read the Excel file. Make sure it is a valid .xlsx file.",
            "error"
          );
        }
      };
      reader.readAsArrayBuffer(file);
    } else {
      // ✅ Parse CSV as plain text
      const reader = new FileReader();
      reader.onload = (event) => {
        const text = event.target?.result;
        if (typeof text !== "string") return;

        const { headers, rows } = parseCSV(text);
        setImportPreview(rows);
        setImportResult(null);
        setImportActiveTab("preview");
        setShowImportModal(true);
      };
      reader.readAsText(file);
    }
  };


  const handleEditCell = (rowIdx, key, value) => {
    const updated = [...importPreview];
    updated[rowIdx][key] = value;
    setImportPreview(updated);
  };

  const handleDeleteRow = (rowIdx) => {
    const updated = importPreview.filter((_, idx) => idx !== rowIdx);
    setImportPreview(updated);
  };

  const handleCloseImportModal = () => {
    setShowImportModal(false);
    setImportPreview([]);
    setImportResult(null);
    setImportActiveTab("preview");
    setDuplicateSelections({});
  };

  const handleDuplicateSelection = (dupIndex, selectedRecord) => {
    setDuplicateSelections((prev) => ({
      ...prev,
      [dupIndex]: selectedRecord,
    }));
  };

  const handleUpdateDuplicate = async (dupIndex, dup, selectedRecord) => {
    if (!selectedRecord) {
      showAlert(language === "ar" ? "تنبيه" : "Alert", language === "ar" ? "يرجى اختيار إما السجل الموجود أو السجل الجديد" : "Please select either Existing or Incoming record", 'error');
      return;
    }

    try {
      const payload = {
        employeeId: dup.employeeId,
        date: dup.date,
        selectedRecord: selectedRecord,
      };

      if (selectedRecord === "incoming" && dup.incoming) {
        payload.incomingData = {
          name: dup.incoming.name,
          checkIn: dup.incoming.checkIn,
          checkOut: dup.incoming.checkOut,
          status: dup.incoming.status,
          notes: dup.incoming.notes,
          workingHours: dup.incoming.workingHours,
          overTime: dup.incoming.overTime,
          underTime: dup.incoming.underTime,
        };
      }

      const response = await apiFetch(
        `${BASE_URL}/attendance/resolve-duplicate`,
        {
          method: 'POST',
          body: JSON.stringify(payload),
          headers: {
            "Accept-Language": language,
          }
        }
      );
      const data = await response.json();

      if (data.success) {
        setImportResult((prev) => ({
          ...prev,
          duplicates: prev.duplicates.filter((_, idx) => idx !== dupIndex),
        }));

        setDuplicateSelections((prev) => {
          const updated = { ...prev };
          delete updated[dupIndex];
          return updated;
        });

        showAlert(language === "ar" ? "نجاح" : "Success", language === "ar" ? "تم حل النسخة المكررة بنجاح!" : "✅ Duplicate resolved successfully!", 'success');
      }
    } catch (error) {
      console.error("❌ Failed to resolve duplicate:", error);
      showAlert(language === "ar" ? "خطأ" : "Error", language === "ar" ? "فشل حل النسخة المكررة. حاول مرة أخرى." : "Failed to resolve duplicate. Please try again.", 'error');
    }
  };

  const handleResolveDuplicatesBulk = async (bulkChoice) => {
    if (!importResult?.duplicates || importResult.duplicates.length === 0) {
      showAlert(language === "ar" ? "تنبيه" : "Alert", language === "ar" ? "لا توجد نسخ مكررة للحل" : "No duplicates to resolve", 'info');
      return;
    }

    setIsResolvingDuplicates(true);
    try {
      const duplicatesPayload = importResult.duplicates.map((dup, idx) => {
        const payload = {
          employeeId: dup.employeeId,
          date: dup.date,
          selectedRecord: bulkChoice,
        };

        if (bulkChoice === "incoming" && dup.incoming) {
          payload.incomingData = {
            name: dup.incoming.name,
            checkIn: dup.incoming.checkIn,
            checkOut: dup.incoming.checkOut,
            status: dup.incoming.status,
            notes: dup.incoming.notes,
            workingHours: dup.incoming.workingHours,
            overTime: dup.incoming.overTime,
            underTime: dup.incoming.underTime,
          };
        }

        return payload;
      });

      console.log(
        `📤 Sending ${duplicatesPayload.length} duplicate resolutions (${bulkChoice})...`
      );

      const response = await apiFetch(
        `${BASE_URL}/attendance/resolve-duplicate`,
        {
          method: 'POST',
          body: JSON.stringify({
            duplicates: duplicatesPayload,
          }),
          headers: {
            "Accept-Language": language,
          }
        }
      );
      const data = await response.json();

      if (data.success) {
        setImportResult((prev) => ({
          ...prev,
          duplicates: [],
        }));

        setDuplicateSelections({});

        showAlert(language === "ar" ? "نجاح" : "Success", language === "ar" ? `تم حل جميع ${duplicatesPayload.length} النسخ المكررة بنجاح!` : `✅ All ${duplicatesPayload.length} duplicates resolved successfully!`, 'success');

        setTimeout(() => {
          if (
            !importResult.duplicates ||
            importResult.duplicates.length === 0
          ) {
            handleCloseImportModal();
            loadAttendance(selectedDate, employees);
          }
        }, 1500);
      }
    } catch (error) {
      console.error("❌ Failed to resolve duplicates:", error);
      const errorMsg = error.response?.data?.error || error.message;
      showAlert(language === "ar" ? "خطأ" : "Error", language === "ar" ? `فشل حل النسخ المكررة: ${errorMsg}` : `Failed to resolve duplicates: ${errorMsg}`, 'error');
    } finally {
      setIsResolvingDuplicates(false);
    }
  };

  const handleProcessImport = async () => {
    if (importPreview.length === 0) {
      showAlert(language === "ar" ? "تنبيه" : "Alert", language === "ar" ? "لا توجد صفوف للاستيراد!" : "No rows to import!", 'warning');
      return;
    }

    setIsImporting(true);
    try {
      const csvContent = serializePreviewToCSV(importPreview);
      const blob = new Blob([csvContent], { type: "text/csv" });
      const file = new File([blob], "attendance_import.csv", {
        type: "text/csv",
      });

      const formData = new FormData();
      formData.append("file", file);

      const currentViewType = viewType || localStorage.getItem('managerViewType') || 'manager';

      const response = await apiFetch(
        `${BASE_URL}/attendance/import/${profile?.infinityCode || localStorage.getItem("infinityCode")}?viewType=${encodeURIComponent(currentViewType)}`,
        {
          method: 'POST',
          body: formData,
          headers: {
            "Accept-Language": language,
            "x-view-type": currentViewType,
          }
        }
      );
      const responseData = await response.json();

      console.log("✅ Import response:", responseData);
      if (responseData && responseData.details) {
        // The backend puts the row lists in `details` and the counts in
        // `summary` — the results tab needs both, so merge them (keeping
        // the arrays; summary.duplicates/errors are numbers).
        const { details, summary = {} } = responseData;
        const processed = details.processed || [];
        const inserted = summary.inserted ?? processed.filter((p) => p.created).length;
        const updated = summary.updated ?? processed.filter((p) => p.updated).length;
        setImportResult({
          ...details,
          inserted,
          updated,
          total:
            inserted +
            updated +
            (details.duplicates?.length ?? 0) +
            (details.errors?.length ?? 0),
        });
      } else {
        setImportResult(responseData);
      }
      setImportActiveTab("results");

      setTimeout(() => {
        loadAttendance(selectedDate, employees);
      }, 1000);
    } catch (error) {
      console.error("❌ Import failed:", error);

      if (error.response?.status === 403 || error.response?.data?.code === 'ACCESS_DENIED') {
        const backendError = error.response?.data?.error || error.message;
        showAlert(
          language === "ar" ? "رفض الوصول" : "Access Denied",
          language === "ar"
            ? `ليس لديك صلاحية لاستيراد حضور بعض الموظفين:\n\n${backendError}`
            : `You don't have permission to import attendance for some employees:\n\n${backendError}`,
          'error'
        );
      } else {
        const errorMsg = error.response?.data?.error || error.response?.data?.message || error.message;
        showAlert(
          language === "ar" ? "خطأ" : "Error",
          language === "ar" ? `فشل الاستيراد: ${errorMsg}` : `Import failed: ${errorMsg}`,
          'error'
        );
      }
    } finally {
      setIsImporting(false);
    }
  };

  return {
    showImportModal,
    setShowImportModal,
    importPreview,
    importResult,
    importActiveTab,
    setImportActiveTab,
    isImporting,
    duplicateSelections,
    isResolvingDuplicates,
    handleImportFileChange,
    handleEditCell,
    handleDeleteRow,
    handleCloseImportModal,
    handleDuplicateSelection,
    handleUpdateDuplicate,
    handleResolveDuplicatesBulk,
    handleProcessImport,
  };
};

export default useCSVImport;